import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { UseCases } from "@planzone/core";
import {
  useEditOrganizationalUnit,
  useMoveOrganizationalUnit,
} from "@planzone/view-models";
import { useModal } from "@root/client/modal";
import { Safeguard, usePermissions } from "@scenes/safeguard";
import { TextInput } from "@common/textInput";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { Preview } from "@scenes/wbs/preview";
import { Draggable, DropBox, useDragAndDrop } from "@common/dragAndDrop";
import { ListToggleArrow } from "@common/listToggleArrow";
import { useToggle } from "@common/treeToggle";
import { ORGANIZATIONAL_UNIT_DRAG_TYPE } from "../dragType";
import { Props } from "./types";
import { EditIcon } from "./editIcon";
import { ResourcesList } from "./resourcesList";
import { DropLine } from "./dropLine";
import styles from "./styles.scss";

function Node({
  organizationalUnit,
  depth,
  ancestorUuids,
  childrenByParent,
  columns,
}: Props) {
  const { t } = useTranslation();
  const { pushView } = useModal();
  const { editOrganizationalUnit } = useEditOrganizationalUnit();
  const { moveOrganizationalUnit } = useMoveOrganizationalUnit();
  const { canMoveOrganizationalUnit, canUpdateOrganizationalUnit } =
    usePermissions([
      UseCases.moveOrganizationalUnit,
      UseCases.updateOrganizationalUnit,
    ]);
  const [draftName, setDraftName] = React.useState<string | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const { isDragging: isTreeDragging } = useDragAndDrop();
  const { isOpen, toggle } = useToggle(organizationalUnit.uuid, depth, true);
  const children = childrenByParent.get(organizationalUnit.uuid) ?? [];
  const resources = organizationalUnit.resources ?? [];

  function openEdition() {
    pushView({
      key: UseCases.updateOrganizationalUnit,
      data: { organizationalUnit },
    });
  }

  function submit() {
    const trimmedName = draftName?.trim();
    trimmedName &&
      trimmedName !== organizationalUnit.name &&
      editOrganizationalUnit({ organizationalUnit, name: trimmedName });
    setDraftName(null);
  }

  return (
    <li className={styles.node}>
      <DropBox
        acceptedTypes={[ORGANIZATIONAL_UNIT_DRAG_TYPE]}
        isDisabled={({ payload }) =>
          !canMoveOrganizationalUnit ||
          payload.organizationalUnit?.uuid === organizationalUnit.uuid ||
          ancestorUuids.includes(payload.organizationalUnit?.uuid)
        }
        drop={({ payload }) =>
          moveOrganizationalUnit({
            organizationalUnit: payload.organizationalUnit,
            newParentUuid: organizationalUnit.uuid,
          })
        }
      >
        {(_, canDrop) => (
          <Draggable
            type={ORGANIZATIONAL_UNIT_DRAG_TYPE}
            data={{ organizationalUnit }}
            isDisabled={!canMoveOrganizationalUnit}
            startDrag={() => setIsDragging(true)}
            stopDrag={() => setIsDragging(false)}
          >
            <div
              className={classNames(styles.rowWrapper, {
                [styles.dragging]: isDragging,
              })}
            >
              <div
                className={classNames(styles.row, styles[`depth${depth}`], {
                  [styles.passThrough]: isTreeDragging,
                })}
                onKeyDown={(e) => e.key === "Escape" && setDraftName(null)}
              >
                <div className={styles.nameCell}>
                  <div className={styles.toggleArrowBox} onClick={toggle}>
                    <ListToggleArrow isOpen={isOpen} />
                  </div>
                  {draftName === null ? (
                    <div className={styles.nameContainer}>
                      <p
                        className={classNames(styles.name, {
                          [styles.clickable]: canUpdateOrganizationalUnit,
                        })}
                        onClick={
                          canUpdateOrganizationalUnit ? openEdition : undefined
                        }
                      >
                        {organizationalUnit.name}
                      </p>
                      <p className={styles.resourcesCount}>
                        {resources.length}
                      </p>
                      <Safeguard useCases={[UseCases.editOrganizationalUnit]}>
                        {({ canEditOrganizationalUnit }) =>
                          canEditOrganizationalUnit && (
                            <button
                              type="button"
                              className={styles.editButton}
                              aria-label={t("obsPage.rename")}
                              onClick={() =>
                                setDraftName(organizationalUnit.name)
                              }
                            >
                              <EditIcon className={styles.icon} />
                            </button>
                          )
                        }
                      </Safeguard>
                    </div>
                  ) : (
                    <form
                      className={styles.editForm}
                      onSubmit={(e) => {
                        e.preventDefault();
                        submit();
                      }}
                    >
                      <TextInput
                        className={styles.editInput}
                        placeholder={t("obsPage.creation.namePlaceholder")}
                        value={draftName}
                        input={setDraftName}
                        blur={submit}
                        autoFocus
                      />
                    </form>
                  )}
                  {draftName === null && (
                    <div className={styles.contextualMenu}>
                      <SecureContextualMenu
                        items={[
                          {
                            name: t("obsPage.edit"),
                            useCase: UseCases.updateOrganizationalUnit,
                            act: openEdition,
                          },
                          {
                            name: t("obsPage.move"),
                            useCase: UseCases.moveOrganizationalUnit,
                            act: () =>
                              pushView({
                                key: UseCases.moveOrganizationalUnit,
                                data: { organizationalUnit },
                              }),
                          },
                          {
                            name: t("obsPage.remove"),
                            color: "#df3b37",
                            useCase: UseCases.removeOrganizationalUnit,
                            act: () =>
                              pushView({
                                key: UseCases.removeOrganizationalUnit,
                                data: { organizationalUnit },
                              }),
                          },
                        ]}
                      />
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                className={styles.addAction}
                aria-label={t("obsPage.addSubUnit")}
                onClick={() =>
                  pushView({
                    key: UseCases.createOrganizationalUnit,
                    data: { parent: organizationalUnit },
                  })
                }
              >
                <span className={styles.addLine} />
                <span className={styles.addBubble}>
                  <span className={styles.addIcon}>+</span>
                </span>
              </button>
              <Preview canDrop={canDrop} isList />
            </div>
          </Draggable>
        )}
      </DropBox>
      {isOpen && (
        <ResourcesList resources={resources} depth={depth} columns={columns} />
      )}
      {isOpen && children.length > 0 && (
        <ul
          className={classNames(styles.children, {
            [styles.stacked]: isTreeDragging,
          })}
        >
          {children.map((child) => (
            <Node
              key={child.uuid}
              organizationalUnit={child}
              depth={depth + 1}
              ancestorUuids={[...ancestorUuids, organizationalUnit.uuid]}
              childrenByParent={childrenByParent}
              columns={columns}
            />
          ))}
        </ul>
      )}
      <DropLine
        organizationalUnit={organizationalUnit}
        ancestorUuids={ancestorUuids}
      />
    </li>
  );
}

export { Node };
